import React, { useState, useRef } from 'react'

import { CSVLink } from 'react-csv'

import { get, set } from 'lodash'

import { DownloadOutlined } from '@ant-design/icons'
import { Button, Tooltip } from 'antd'

import { observer } from 'mobx-react-lite'

import { Box } from 'common/components/boxes'
import { formatFullAddress } from 'common/helpers/formatters'
import { formatForCSVExport } from 'common/helpers/formatters'
import { OrderStates } from 'common/server/server_types'

import { useVendorStores } from 'vendor/hooks/use-stores'

const headers = [
  { label: 'Description', key: 'description' },
  { label: 'Quantity', key: 'quantity' },
  { label: 'Product ID', key: 'product_identifier' },
  { label: 'Manufacturer', key: 'manufacturer' },
  { label: 'Unit', key: 'unit' },
  { label: 'Unit Cost', key: 'unit_cost' },
  { label: 'Vendor Notes', key: 'vendor_note' },
  { label: 'Address', key: 'full_address' },
]

type DownloadOrderProps = {
  orderId: string
  isDropdownItem?: boolean
  state?: OrderStates
}

export const DownloadOrder = observer(({ orderId, isDropdownItem = false, state = null }: DownloadOrderProps) => {
  const { orderStore } = useVendorStores()

  const csvInstance = useRef(null)

  const [isLoading, setLoading] = useState(false)
  const [orderNumber, setOrderNumber] = useState('order')
  const [data, setData] = useState([])

  const handleDownload = async (event) => {
    event.stopPropagation()
    try {
      setLoading(true)
      const order = await orderStore.selectOrder(orderId)

      const data =
        order?.order_materials.map((material) => {
          const delivery = order?.deliveries.find((delivery) => delivery.id === material.delivery_id)

          const full_address = delivery?.address ? formatFullAddress(delivery?.address) : ''

          return {
            description: material.company_material.description,
            product_identifier: material.company_material.product_identifier,
            unit: material.company_material.unit,
            manufacturer: material.company_material.manufacturer,
            quantity: material.quantity,
            unit_cost: material.unit_cost,
            vendor_note: material.vendor_note,
            full_address,
          }
        }) || []

      data.forEach((hit) => {
        headers.forEach(({ key }) => {
          const currentValue = get(hit, key)
          const newValue = formatForCSVExport(currentValue)
          set(hit, key, newValue)
        })
      })

      setOrderNumber(order.order_number)
      setData(data)
      csvInstance.current?.link?.click()
    } finally {
      setLoading(false)
    }
  }

  const download = <CSVLink data={data} filename={`${orderNumber}.csv`} headers={headers} ref={csvInstance} />

  if (isDropdownItem) {
    return (
      <span onClick={(event) => event.stopPropagation()}>
        <span onClick={handleDownload}>Download {state == OrderStates.QUOTED ? 'RFQ' : 'Order'}</span>
        {download}
      </span>
    )
  }

  return (
    <Tooltip title="Download order">
      <Box onClick={(event) => event.stopPropagation()}>
        <Button data-cy="download-order" icon={<DownloadOutlined />} loading={isLoading} onClick={handleDownload}>
          Download
        </Button>
        {download}
      </Box>
    </Tooltip>
  )
})
